import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  CreditCard, 
  ExternalLink, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { StripeEmbeddedOnboarding } from './StripeEmbeddedOnboarding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StripeConnectCard({ crafterId, crafterEmail, hideWhenConnected = false, variant = 'orders', onStatusChange }) {
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [accountStatus, setAccountStatus] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  // Text content based on variant
  const content = {
    orders: {
      title: "Collega il tuo conto bancario",
      description: "Per ricevere i pagamenti delle tue vendite devi impostare il tuo conto. I fondi verranno trasferiti automaticamente quando i clienti confermeranno la ricezione.",
      buttonText: "Imposta il tuo conto"
    },
    products: {
      title: "Collega il tuo conto bancario",
      description: "Prima di iniziare ad inserire i prodotti nel tuo negozio, è necessario configurare il tuo conto. Questo passaggio è indispensabile per ricevere i versamenti delle tue vendite in automatico sul tuo conto bancario.",
      buttonText: "Imposta il tuo conto"
    }
  };

  const texts = content[variant] || content.orders;

  useEffect(() => {
    if (crafterId) {
      checkAccountStatus();
    }
  }, [crafterId]);

  const checkAccountStatus = async (silent = false) => {
    if (!silent) setCheckingStatus(true);
    try {
      const response = await fetch(`${API_URL}/api/connect/account-status/${crafterId}`);
      if (response.ok) {
        const data = await response.json();
        setAccountStatus(data);
        return data;
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
    } finally {
      if (!silent) setCheckingStatus(false);
    }
    return null;
  };

  const handleConnectStripe = () => {
    // Onboarding EMBEDDED: l'artigiano resta su Rarifex. Il componente
    // StripeEmbeddedOnboarding chiama /api/connect/account-session che
    // crea l'account Express e ritorna il client_secret per la sessione.
    setOnboardingOpen(true);
  };

  const handleOnboardingComplete = async () => {
    setOnboardingOpen(false);
    toast.success('Verifica in corso da parte di Stripe...');
    // Polling: aggiorna stato locale E genitore finché l'account risulta attivo
    // (o max tentativi), così tutta la sezione diventa verde senza refresh manuale.
    for (let i = 0; i < 6; i++) {
      const data = await checkAccountStatus(i > 0);
      if (onStatusChange) onStatusChange(data);
      if (data?.has_account && data?.charges_enabled) break;
      await new Promise((r) => setTimeout(r, 2000));
    }
  };

  const handleManualRefresh = async () => {
    const data = await checkAccountStatus();
    if (onStatusChange) onStatusChange(data);
  };

  const handleOpenDashboard = async () => {
    setDashboardLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/connect/dashboard-link/${crafterId}`);
      
      if (!response.ok) {
        throw new Error('Failed to get dashboard link');
      }

      const data = await response.json();
      window.open(data.url, '_blank');
      
    } catch (error) {
      console.error('Dashboard link error:', error);
      toast.error('Errore nell\'apertura della dashboard Stripe');
    } finally {
      setDashboardLoading(false);
    }
  };

  // Dialog embedded onboarding (visibile da qualsiasi branch)
  const onboardingDialogJSX = (
    <Dialog open={onboardingOpen} onOpenChange={setOnboardingOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stripe-onboarding-dialog">
        <DialogHeader>
          <DialogTitle>Attivazione pagamenti</DialogTitle>
        </DialogHeader>
        <StripeEmbeddedOnboarding
          crafterId={crafterId}
          email={crafterEmail}
          onComplete={handleOnboardingComplete}
          onExit={() => setOnboardingOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );

  if (checkingStatus) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-[#008080]" />
          <span className="text-slate-600">Verifica stato Stripe...</span>
        </div>
      </Card>
    );
  }

  // Account connected and active
  if (accountStatus?.has_account && accountStatus?.charges_enabled) {
    // If hideWhenConnected is true, don't render anything when connected
    if (hideWhenConnected) {
      return null;
    }
    return (
      <Card className="p-6 border-green-200 bg-green-50/50">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                Stripe Connesso
                <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                  Attivo
                </Badge>
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Il tuo account è configurato per ricevere pagamenti.
                I fondi verranno trasferiti quando i clienti confermeranno la ricezione degli ordini.
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleOpenDashboard}
            disabled={dashboardLoading}
          >
            {dashboardLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Dashboard Stripe
                <ExternalLink className="w-3 h-3 ml-1" />
              </>
            )}
          </Button>
        </div>
      </Card>
    );
  }

  // Account created but onboarding incomplete
  if (accountStatus?.has_account && !accountStatus?.charges_enabled) {
    return (
      <>
        {onboardingDialogJSX}
        <Card className="p-6 border-amber-200 bg-amber-50/50">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                Configurazione Incompleta
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                  In attesa
                </Badge>
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Completa la configurazione del tuo account Stripe per iniziare a ricevere pagamenti.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleManualRefresh}
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button 
              size="sm"
              className="bg-[#008080] text-white"
              onClick={handleConnectStripe}
              disabled={loading}
              data-testid="stripe-complete-config-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Completa Configurazione
                  <ExternalLink className="w-3 h-3 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
      </>
    );
  }

  // No account - show connect button
  return (
    <Card className="p-6 border-[#008080]/20 bg-gradient-to-br from-[#008080]/5 to-transparent">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-[#008080]/10 rounded-xl flex items-center justify-center">
          <CreditCard className="w-6 h-6 text-[#008080]" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-800">{texts.title}</h3>
          <p className="text-sm text-slate-600 mt-1 mb-4">
            {texts.description}
          </p>
          <div className="flex items-center gap-4">
            <Button 
              className="bg-[#008080] hover:bg-[#006666] text-white"
              onClick={handleConnectStripe}
              disabled={loading}
              data-testid="connect-stripe-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Connessione...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {texts.buttonText}
                </>
              )}
            </Button>
            <span className="text-xs text-slate-500">
              Processo sicuro gestito da Stripe
            </span>
          </div>
        </div>
      </div>

      {/* Dialog embedded onboarding - l'artigiano resta su Rarifex */}
      {onboardingDialogJSX}
    </Card>
  );
}
